import { Link, useParams } from 'react-router-dom'
import { PageWrapper } from '../components/layout/PageWrapper'
import { Seo } from '../components/Seo'
import { ARTICLE_BY_SLUG, resolveText } from '../lib/helpArticles'
import { useAppConfig } from '../hooks/useAppConfig'
import { NotFound } from './NotFound'

export function HelpArticle() {
  const { slug } = useParams<{ slug: string }>()
  const { paywallEnabled } = useAppConfig()
  const article = slug ? ARTICLE_BY_SLUG[slug] : undefined
  if (!article) return <NotFound />

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        datePublished: article.updated,
        dateModified: article.updated,
        author: { '@type': 'Organization', name: 'FixDelivered' },
        publisher: { '@type': 'Organization', name: 'FixDelivered', url: 'https://fixdelivered.com/' },
        mainEntityOfPage: `https://fixdelivered.com/help/${article.slug}`,
      },
      {
        '@type': 'FAQPage',
        mainEntity: article.faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: resolveText(f.a, paywallEnabled) },
        })),
      },
    ],
  }

  return (
    <PageWrapper>
      <Seo
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/help/${article.slug}`}
        breadcrumbs={[
          { name: 'Help', url: '/help' },
          { name: article.title, url: `/help/${article.slug}` },
        ]}
        structuredData={structuredData}
      />

      <article className="max-w-3xl mx-auto px-4 py-12">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold-500 mb-2">
          <Link to="/help" className="hover:underline">Help &amp; advice</Link>
        </p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{article.title}</h1>
        <p className="text-xs text-gray-400 mb-8">
          Updated {new Date(article.updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        {article.intro.map((p, i) => (
          <p key={i} className="text-gray-700 leading-relaxed mb-4 text-lg">{p}</p>
        ))}

        {article.sections.map(section => (
          <section key={section.h2} className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-3">{section.h2}</h2>
            {section.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-4">{resolveText(p, paywallEnabled)}</p>
            ))}
          </section>
        ))}

        {/* CTA */}
        <div className="mt-10 bg-gray-900 text-white rounded-2xl p-8 text-center">
          <p className="text-lg font-bold mb-1">Need a plumber right now?</p>
          <p className="text-gray-300 text-sm mb-5">
            {paywallEnabled
              ? 'We find and confirm a qualified local plumber, usually within the hour — or your booking fee back.'
              : 'We find and confirm a qualified local plumber, usually within the hour — completely free right now, no booking fee.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-emergency-600 hover:bg-emergency-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Get help now →
          </Link>
        </div>

        {/* FAQs */}
        <h2 className="text-2xl font-bold text-gray-900 mt-14 mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {article.faqs.map(f => (
            <div key={f.q} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-1.5">{f.q}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{resolveText(f.a, paywallEnabled)}</p>
            </div>
          ))}
        </div>
      </article>
    </PageWrapper>
  )
}
